import { ref, type Ref } from "vue";

import { getPaymentsLog } from "../api";
import type {
  AdminV1OrganizationsPaymentsLogListParams,
  PaginatedPaymentsLogList,
  PaymentsLog,
} from "@/api/model";
import type { PaymentsLogFilters } from "@/types";

const PAGE_SIZE = 20;
const FIRST_PAGE = 0;

export function usePaymentsLog(): {
  paginatedPayments: Ref<PaginatedPaymentsLogList | null>;
  fetchPayments: () => Promise<void>;
  buildFilterParams: () => AdminV1OrganizationsPaymentsLogListParams;
  paymentFetchError: Ref<string | null>;
  isLoading: Ref<boolean>;
  currentPage: Ref<number>;
  pageSize: Ref<number>;
  totalRecords: Ref<number | undefined>;
  filter: Ref<PaymentsLogFilters | null>;
  searchQuery: Ref<string>;
  typeFilter: Ref<string | null>;
  statusFilter: Ref<string | null>;
  sortField: Ref<string>;
} {
  const pageSize = ref(PAGE_SIZE);
  const currentPage = ref(FIRST_PAGE);
  const totalRecords = ref<number | undefined>(0);
  const filter: Ref<PaymentsLogFilters | null> = ref<PaymentsLogFilters | null>(
    "paid"
  );

  const searchQuery = ref("");
  const typeFilter = ref<string | null>(null);
  const statusFilter = ref<string | null>(null);
  const sortField = ref<string>("-payment_date");

  const paymentFetchError = ref<string | null>(null);
  const paginatedPayments = ref<PaginatedPaymentsLogList | null>(null);
  const isLoading = ref(false);

  /**
   * The active filter/search/sort as query params, without pagination.
   *
   * Shared by the table fetch and the CSV export so the export can only ever
   * describe the rows the table is showing. Adding a filter here reaches both;
   * duplicating this mapping at the call site is how the two drift apart.
   */
  function buildFilterParams(): AdminV1OrganizationsPaymentsLogListParams {
    const params: AdminV1OrganizationsPaymentsLogListParams = {};

    if (filter.value && filter.value !== null) {
      params.filter = filter.value;
    }

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (typeFilter.value) {
      params.type = typeFilter.value as any;
    }

    if (statusFilter.value) {
      params.status = statusFilter.value as any;
    }

    if (sortField.value) {
      params.ordering = sortField.value;
    }

    return params;
  }

  async function fetchPayments() {
    isLoading.value = true;
    paymentFetchError.value = null;

    try {
      const offset = currentPage.value * pageSize.value;
      const params: AdminV1OrganizationsPaymentsLogListParams = {
        limit: pageSize.value,
        offset: offset,
        ...buildFilterParams(),
      };

      const results = await getPaymentsLog(params);

      if ("error" in results) {
        paymentFetchError.value = results.error;
      } else {
        paginatedPayments.value = results;
        totalRecords.value = results.count;
      }
    } finally {
      isLoading.value = false;
    }
  }

  return {
    paginatedPayments,
    fetchPayments,
    buildFilterParams,
    paymentFetchError,
    isLoading,
    currentPage,
    pageSize,
    totalRecords,
    filter,
    searchQuery,
    typeFilter,
    statusFilter,
    sortField,
  };
}
